import { c as _c } from "react/compiler-runtime";
import { LinkButton, LoadingSpinner, PageShell } from '@onefootprint/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import useD2pStatusPoll, { isTerminalStatus } from '@/idv2/hooks/use-d2p-status-poll';
import { useFootprint } from '@/idv2/hooks/use-footprint';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo,
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'passkey-processing'
});
const processingSearchSchema = z.object({
  authToken: z.string(),
  validationToken: z.string(),
  scopedAuthToken: z.string()
});
export const Route = createFileRoute('/identify/passkey-processing')({
  validateSearch: processingSearchSchema,
  component: PasskeyProcessingRoute
});
function PasskeyProcessingRoute() {
  const $ = _c(30);
  const {
    validationToken,
    scopedAuthToken
  } = Route.useSearch();
  const footprint = useFootprint();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "identify.passkeyProcessing"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t0);
  const hasProceedRef = useRef(false);
  let t1;
  if ($[1] !== footprint || $[2] !== validationToken) {
    t1 = () => {
      if (hasProceedRef.current) {
        return;
      }
      hasProceedRef.current = true;
      logInfo("Passkey processing complete, proceeding");
      footprint.navigation.identifyComplete(validationToken);
    };
    $[1] = footprint;
    $[2] = validationToken;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const proceedToDone = t1;
  let t2;
  if ($[4] !== proceedToDone) {
    t2 = status => {
      if (status === "completed") {
        logInfo("Passkey registration completed via D2P");
        proceedToDone();
      } else {
        if (status === "canceled") {
          logInfo("Passkey registration cancelled via D2P");
          proceedToDone();
        } else {
          if (status === "failed") {
            logWarn("Passkey D2P session failed");
            proceedToDone();
          }
        }
      }
    };
    $[4] = proceedToDone;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== scopedAuthToken || $[7] !== t2) {
    t3 = {
      authToken: scopedAuthToken,
      onStatusChange: t2
    };
    $[6] = scopedAuthToken;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const d2pPoll = useD2pStatusPoll(t3);
  let t4;
  if ($[9] !== d2pPoll || $[10] !== proceedToDone) {
    t4 = async () => {
      logInfo("Cancelling passkey D2P session");
      ;
      try {
        await d2pPoll.cancel();
      } catch (t5) {
        const error = t5;
        logWarn("Failed to cancel D2P session", error);
      }
      proceedToDone();
    };
    $[9] = d2pPoll;
    $[10] = proceedToDone;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleCancel = t4;
  let t5;
  if ($[12] !== d2pPoll || $[13] !== handleCancel) {
    t5 = () => {
      const passkeyWindow = (window as Window & {
        __fp_passkey_window?: Window;
      }).__fp_passkey_window;
      if (!passkeyWindow || passkeyWindow.closed) {
        return;
      }
      const checkInterval = window.setInterval(() => {
        if (passkeyWindow.closed) {
          window.clearInterval(checkInterval);
          if (isTerminalStatus(d2pPoll.status)) {
            return;
          }
          handleCancel();
        }
      }, 1000);
      return () => window.clearInterval(checkInterval);
    };
    $[12] = d2pPoll;
    $[13] = handleCancel;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  useEffect(t5, t6);
  let t7;
  if ($[16] !== t) {
    t7 = t(_temp);
    $[16] = t;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t) {
    t8 = t(_temp2);
    $[18] = t;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <LoadingSpinner size="xlarge" />;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t) {
    t10 = t(_temp3);
    $[21] = t;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== handleCancel || $[24] !== t10) {
    t11 = <div className="flex flex-col items-center pt-6 gap-6">{t9}<LinkButton onClick={handleCancel}>{t10}</LinkButton></div>;
    $[23] = handleCancel;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11 || $[27] !== t7 || $[28] !== t8) {
    t12 = <PageShell title={t7} subtitle={t8}>{t11}</PageShell>;
    $[26] = t11;
    $[27] = t7;
    $[28] = t8;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
}
function _temp3($_1) {
  return $_1.cancel;
}
function _temp2($_0) {
  return $_0.subtitle;
}
function _temp($) {
  return $.title;
}